import { useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { SectionEditDrawer } from '@/components/common/SectionEditDrawer';
import { useUploadsInFlight } from '@/hooks/useUploadsInFlight';
import { useRetainedValue } from '@/hooks/useRetainedValue';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type {
  StartupProfileData,
  StartupProfileUpdatePayload,
} from '../../api/startup-profile.api';
import {
  startupProfileSchema,
  toApiPayload,
  toFormValues,
  type StartupProfileFormValues,
} from './schema';
import { useSaveStartupProfile } from './useStartupProfileMutations';
import { STARTUP_SECTIONS, type StartupSectionId } from './startup-sections';
import { STARTUP_SECTION_EDITORS } from './StartupSectionContent';
import { STARTUP_FIELD_LABELS } from './startup-field-labels';

const S = UI_STRINGS.STARTUP_PROFILE;

export interface StartupSectionEditorProps {
  data: StartupProfileData;
  entityId: string;
  /** The open section, or null when no panel is open. */
  section: StartupSectionId | null;
  onClose: () => void;
}

/** Holds a section's payload behind the "Are you sure?" step, then saves it (FR-173). */
function useConfirmedSave(entityId: string, onClose: () => void) {
  const [pending, setPending] = useState<StartupProfileUpdatePayload | null>(null);
  const save = useSaveStartupProfile(entityId, {
    onSaved: () => {
      onClose();
      toast.success(S.SAVE_SUCCESS);
    },
  });
  const confirm = () => {
    if (pending) save.mutate(pending);
    setPending(null);
  };
  return { save, pending, setPending, confirm };
}

/**
 * The startup profile's side-panel editor (Flow 1): one section's fields, the
 * "Are you sure?" step before saving (FR-173), and the save itself.
 */
export function StartupSectionEditor({
  data,
  entityId,
  section,
  onClose,
}: StartupSectionEditorProps) {
  const uploads = useUploadsInFlight();
  const { save, pending, setPending, confirm } = useConfirmedSave(entityId, onClose);
  const shown = useRetainedValue<StartupSectionId>(section, 'company');
  const config = STARTUP_SECTIONS[shown];
  const defaultValues = useMemo(() => toFormValues(data), [data]);

  const { reset } = save;
  useEffect(() => {
    if (section) reset();
  }, [section, reset]);

  return (
    <>
      <SectionEditDrawer<StartupProfileFormValues>
        open={section !== null}
        onClose={onClose}
        title={config.title}
        schema={startupProfileSchema}
        fields={config.fields}
        fieldLabels={STARTUP_FIELD_LABELS}
        defaultValues={defaultValues}
        onSubmit={(values) => setPending(toApiPayload(values, data))}
        isSaving={save.isPending}
        errorMessage={save.isError ? getApiErrorMessage(save.error, S.SAVE_ERROR) : undefined}
        saveDisabled={uploads.isUploading}
      >
        {(form) => STARTUP_SECTION_EDITORS[shown]({ data, form, trackUpload: uploads.track })}
      </SectionEditDrawer>
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={S.CONFIRM_SAVE_TITLE}
        description={S.CONFIRM_SAVE_BODY}
        confirmLabel={S.CONFIRM_SAVE_ACTION}
        onConfirm={confirm}
      />
    </>
  );
}
